/**
 * Transforms API Product data to UI Product format
 * Used by ProductGrid and ProductCard components
 */

import type {
  Product as ApiProduct,
  ProductColor as ApiProductColor,
  ProductCategory as ApiProductCategory,
  ProductMockupTemplate as ApiMockupTemplate,
  MockupBlendMode,
} from '@/api/products';

import {
  DEFAULT_MOCKUP_LAYER_ORDER,
  ProductPosition,
  effectiveSizeTier,
  isCalibrated,
  isFinitePositive,
  normalizeMockupLayerOrder,
  effectiveCarryOver,
  templateRect,
  type TemplateRect,
  type CarryOverAnchor,
  type MatchPosition,
  type PlacementAreaProduct,
  type ProductClass,
  type SizeTier,
} from '@designdeck/shared';
import { getDecorationCategoryFromCode } from '@/lib/decoration-method';

// ═══════════════════════════════════════════════════════════════════
// UI TYPES (used by ProductCard component)
// ═══════════════════════════════════════════════════════════════════

export interface UIProductColor {
  id: string;
  name: string;
  hex: string;
  isHeathered: boolean;
}

export interface UIProductView {
  id: string;
  name: string;
  label: string;
}

/**
 * The layers of a mockup template, without any print area geometry: what
 * a mockup preview needs to draw the product. The admin walkthrough draws
 * templates that are not calibrated yet through this shape.
 */
export interface UIMockupLayers {
  id: string;
  name: string;
  positionKey: string; // "front", "back", "back-inside"
  productPositionId: string | null; // The actual ProductPosition.id for backend API
  mockupType: string;

  // Base Image (REQUIRED): the shading map of the garment, composited above
  // the design per layerOrder (below it for embroidery)
  imageUrl: string;
  /** Opacity of the shading map (Shirtigo "Deckkraft Schatten-Layer") */
  baseOpacity: number;
  /** Opacity of the design layer */
  designOpacity: number;

  // Native mockup image dimensions (the canvas coordinate system)
  mockupWidthPx: number;
  mockupHeightPx: number;

  // Layer 3: Heathered Overlay (OPTIONAL)
  heatheredOverlayUrl: string | null;
  heatheredBlendMode: MockupBlendMode;
  heatheredOpacity: number;

  // Layer 4: Texture Overlay (OPTIONAL)
  textureOverlayUrl: string | null;
  textureBlendMode: MockupBlendMode;
  textureOpacity: number;

  // Layer 5: Shadow Overlay (OPTIONAL) - pinned above the ordered stack
  shadowOverlayUrl: string | null;
  shadowBlendMode: MockupBlendMode;
  shadowOpacity: number;

  // Layer 6: Cutout Mask (OPTIONAL)
  cutoutMaskUrl: string | null;

  // Card-sized (480 px) variants of base and cutout mask for the product
  // cards; null until `mockups:card-variants` rendered them (fallback: full)
  cardImageUrl: string | null;
  cardCutoutMaskUrl: string | null;

  // Overlay (OPTIONAL) - above the whole stack and above the cutout mask,
  // never clipped (frames, clips, hangers, case edges)
  topOverlayUrl: string | null;
  /**
   * Garment parts in front of the print — drawstrings above all. Drawn in the
   * product colour above the design so a print cannot run across them.
   */
  toplayerMaskUrl: string | null;
  /**
   * High-resolution rendering assets (e.g. 2000 px, same aspect ratio as the
   * creator assets). Used where exactly one mockup is shown large: the editor
   * canvas and the admin preview. Cards and previews keep the creator assets.
   */
  renderingImageUrl: string | null;
  renderingCutoutMaskUrl: string | null;

  // Thumbnail for selection
  thumbnailUrl: string | null;

  // Flags
  isDefault: boolean;
  isCreatorEditable: boolean;

  /** Render order of the layer stack, bottom → top */
  layerOrder: string[];
}

/**
 * A calibrated mockup template of a configured position (R12, R31): the
 * layers plus where the print area sits on this template. The rectangle
 * comes from the one formula `templateRect` over the area's millimetres
 * and this template's Pixel Scale and anchors (R7); no consumer recomputes
 * it, and no template without a calibration reaches the UI.
 */
export interface UIProductMockupTemplate extends UIMockupLayers {
  /** Pixel Scale of this template: pixels per mm at mockupWidthPx (R4) */
  pxPerMm: number;
  /** The print area on this template, native pixels (templateRect) */
  printAreaRect: TemplateRect;
  /** Physical print area size in mm (the position is configured, R31) */
  printAreaWidthMm: number;
  printAreaHeightMm: number;
  /** Effective size tier of the print area (override ?? derived) */
  printAreaSizeTier?: SizeTier;
  /** Admin marker RUNDUM (a KLEIN value reads as percent 100, centred) */
  printAreaSizeTierOverride?: SizeTier | null;
  /**
   * The product this template belongs to: the identity of its print area in
   * the carry-over rule (rule 1).
   */
  productId?: string;
  /** Class of that product, when known (rules 3a/3b) */
  printAreaProductClass?: ProductClass | null;
  /** Effective carry-over percent of the area (area exception, else product) */
  printAreaCarryOverPercent?: number | null;
  /** Effective vertical anchor; null means class default */
  printAreaCarryOverAnchor?: CarryOverAnchor | null;
  /** Effective top offset in mm for anchor TOP; null means the default */
  printAreaCarryOverTopOffsetMm?: number | null;

  printArea: object;
}

export interface UIProduct {
  id: string;
  name: string;
  /** Blank reference price for the tenant; null when a cost is not maintained. */
  blankCost: number | null;
  /** Cheapest blank plus one front side, net; null when unknown. */
  startingCost: number | null;
  /** Catalogue recommendation (recommendedSalesPrice); not the seller's price. */
  sellingPrice?: number;
  colors: UIProductColor[];
  selectedColors: string[];
  isActive: boolean;
  isPremium?: boolean;
  category: string;
  categoryId?: string;
  /** HS code from customs classification — "42029900" = phone cover/case */
  hsCode?: string | null;
  views?: UIProductView[];
  thumbnailUrl?: string;
  mockupImageUrl?: string;
  mockupTemplates: UIProductMockupTemplate[];
  positions: ProductPosition[];
  /** Maker of the blank, for the step-2 manufacturer filter. */
  manufacturerId?: string;
  manufacturerName?: string;
  /** Carry-over class; null means unknown (class rules stay off). */
  productClass?: ProductClass | null;
  /** Product-level carry-over settings (areas may override each). */
  carryOverPercent?: number | null;
  carryOverAnchor?: CarryOverAnchor | null;
  carryOverTopOffsetMm?: number | null;
  /**
   * Per Position: physical Print Area width and decoration category, the
   * inputs of the shared "does this product carry the design" predicate.
   */
  matchPositions: MatchPosition[];
}

// ═══════════════════════════════════════════════════════════════════
// STORAGE URL RESOLUTION
// ═══════════════════════════════════════════════════════════════════

/**
 * Get the storage base URL from environment.
 * This allows the same DB to work across dev/staging/prod environments.
 *
 * Examples:
 *   Dev:  http://localhost:9004/designdeck
 *   Prod: https://cdn.designdeck.app
 */
function getStorageBaseUrl(): string {
  return import.meta.env.VITE_STORAGE_URL || 'http://localhost:9004/designdeck';
}

/**
 * Build full storage URL from relative path.
 * DB stores only relative paths (e.g., "mockups/xxx/base.webp").
 * This function builds the full URL using VITE_STORAGE_URL.
 *
 * @param path - Relative path or full URL from DB
 * @returns Full URL for browser to load
 */
export function proxyStorageUrl(
  path: string | null | undefined
): string | null {
  if (!path) return null;

  // If already a full URL (legacy data or external), return as-is
  if (path.startsWith('http://') || path.startsWith('https://')) {
    // Legacy: Transform old localhost:3000/storage URLs to new format
    const legacyPattern = /^https?:\/\/localhost:3000\/storage\/designdeck\//;
    if (legacyPattern.test(path)) {
      const relativePath = path.replace(legacyPattern, '');
      return `${getStorageBaseUrl()}/${relativePath}`;
    }

    // Legacy: Transform old localhost:9000 MinIO URLs
    const minioPattern = /^https?:\/\/localhost:9000\/designdeck\//;
    if (minioPattern.test(path)) {
      const relativePath = path.replace(minioPattern, '');
      return `${getStorageBaseUrl()}/${relativePath}`;
    }

    // Local IP storage URL (dev) - convert to Vite proxy path to avoid mixed content
    const localIpPattern =
      /^https?:\/\/\d+\.\d+\.\d+\.\d+(?::\d+)?\/designdeck\//;
    if (localIpPattern.test(path)) {
      const relativePath = path.replace(localIpPattern, '');
      return `${getStorageBaseUrl()}/${relativePath}`;
    }

    // External URL (e.g., Shirtigo CDN) - return as-is
    return path;
  }

  // Relative path - build full URL from storage base
  return `${getStorageBaseUrl()}/${path}`;
}

// ═══════════════════════════════════════════════════════════════════
// TRANSFORMER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════

/**
 * Transform API ProductColor to UI ProductColor
 */
export function transformColor(apiColor: ApiProductColor): UIProductColor {
  return {
    id: apiColor.id,
    name: apiColor.name,
    hex: apiColor.hexCode,
    isHeathered: apiColor.isHeathered ?? false,
  };
}

/**
 * PrintArea data for canvas sizing
 */
export interface PrintAreaData {
  maxWidthMm?: number; // Physical width in mm
  maxHeightMm?: number; // Physical height in mm
  /** Effective size tier (override ?? derived) */
  sizeTier: SizeTier;
  /** Admin marker RUNDUM (a KLEIN value reads as percent 100, centred) */
  sizeTierOverride: SizeTier | null;
  /** Class of the owning product, when known */
  productClass?: ProductClass | null;
  /** Effective carry-over settings: the area's exception, else the product's */
  carryOverPercent?: number | null;
  carryOverAnchor?: CarryOverAnchor | null;
  carryOverTopOffsetMm?: number | null;
  printArea: object;
}

/**
 * The layers of an API template, proxied for CORS; no geometry. The admin
 * walkthrough previews templates through this before they are calibrated.
 */
export function transformMockupLayers(
  apiTemplate: ApiMockupTemplate,
  positionIdMap: Map<string, string> = new Map()
): UIMockupLayers {
  return {
    id: apiTemplate.id,
    name: apiTemplate.name,
    positionKey: apiTemplate.positionKey,
    productPositionId: positionIdMap.get(apiTemplate.positionKey) || null,
    mockupType: apiTemplate.mockupType || 'flat',

    // Base Image (proxied for CORS) + its opacity as the shading layer
    imageUrl: proxyStorageUrl(apiTemplate.imageUrl) || apiTemplate.imageUrl,
    baseOpacity: apiTemplate.baseOpacity ?? 1,
    designOpacity: apiTemplate.designOpacity ?? 1,

    // Native mockup image dimensions (a column with a database default)
    mockupWidthPx: apiTemplate.mockupWidthPx,
    mockupHeightPx: apiTemplate.mockupHeightPx,

    // Layer 3: Heathered Overlay (proxied for CORS)
    heatheredOverlayUrl: proxyStorageUrl(apiTemplate.heatheredOverlayUrl),
    heatheredBlendMode: apiTemplate.heatheredBlendMode || 'OVERLAY',
    heatheredOpacity: apiTemplate.heatheredOpacity ?? 0.5,

    // Layer 4: Texture Overlay (proxied for CORS)
    textureOverlayUrl: proxyStorageUrl(apiTemplate.textureOverlayUrl),
    textureBlendMode: apiTemplate.textureBlendMode || 'MULTIPLY',
    textureOpacity: apiTemplate.textureOpacity ?? 0.4,

    // Layer 5: Shadow Overlay (proxied for CORS)
    shadowOverlayUrl: proxyStorageUrl(apiTemplate.shadowOverlayUrl),
    shadowBlendMode: apiTemplate.shadowBlendMode || 'MULTIPLY',
    shadowOpacity: apiTemplate.shadowOpacity ?? 0.3,

    // Layer 6: Cutout Mask (proxied for CORS)
    cutoutMaskUrl: proxyStorageUrl(apiTemplate.cutoutMaskUrl),
    cardImageUrl: proxyStorageUrl(apiTemplate.cardImageUrl),
    cardCutoutMaskUrl: proxyStorageUrl(apiTemplate.cardCutoutMaskUrl),

    // Overlay above the mask (proxied for CORS)
    topOverlayUrl: proxyStorageUrl(apiTemplate.topOverlayUrl),
    toplayerMaskUrl: proxyStorageUrl(apiTemplate.toplayerMaskUrl),
    renderingImageUrl: proxyStorageUrl(apiTemplate.renderingImageUrl),
    renderingCutoutMaskUrl: proxyStorageUrl(apiTemplate.renderingCutoutMaskUrl),

    // Thumbnail (proxied for CORS)
    thumbnailUrl: proxyStorageUrl(apiTemplate.thumbnailUrl),

    // Flags
    isDefault: apiTemplate.isDefault ?? false,
    isCreatorEditable: apiTemplate.isCreatorEditable ?? true,
    layerOrder: normalizeLayerOrder(apiTemplate.layerOrder),
  };
}

/**
 * Transform API MockupTemplate to UI MockupTemplate
 * All image URLs are proxied through Vite in development for CORS support.
 *
 * The server sends calibrated templates of configured positions only
 * (R12, R30); this is the client's line of defence. A template without a
 * calibration, or on a position whose print area lacks its millimetres,
 * has no rectangle: it is dropped (null) and logged, never drawn on a
 * made-up area.
 *
 * @param apiTemplate - The mockup template from API
 * @param positionIdMap - Map of positionKey (slug) to productPositionId
 * @param printAreaMap - Map of positionKey (slug) to PrintArea dimensions
 */
export function transformMockupTemplate(
  apiTemplate: ApiMockupTemplate,
  positionIdMap: Map<string, string> = new Map(),
  printAreaMap: Map<string, PrintAreaData> = new Map(),
  /** The owning product; its id names the template's print area. */
  product?: PlacementAreaProduct | null
): UIProductMockupTemplate | null {
  const printArea = printAreaMap.get(apiTemplate.positionKey);
  const widthMm = printArea?.maxWidthMm;
  const heightMm = printArea?.maxHeightMm;
  if (
    !isCalibrated(apiTemplate) ||
    !isFinitePositive(apiTemplate.pxPerMm) ||
    !isFinitePositive(apiTemplate.mockupWidthPx) ||
    !isFinitePositive(widthMm) ||
    !isFinitePositive(heightMm)
  ) {
    console.error(
      `[product-transformer] Template ${apiTemplate.id} (${apiTemplate.positionKey}) dropped: not calibrated or its print area has no millimetres`
    );
    return null;
  }

  const printAreaRect = templateRect(
    { maxWidthMm: widthMm, maxHeightMm: heightMm },
    {
      pxPerMm: apiTemplate.pxPerMm,
      offsetXCenterPx: apiTemplate.offsetXCenterPx ?? 0,
      offsetYTopPx: apiTemplate.offsetYTopPx ?? 0,
      mockupWidthPx: apiTemplate.mockupWidthPx,
    }
  );

  return {
    ...transformMockupLayers(apiTemplate, positionIdMap),
    pxPerMm: apiTemplate.pxPerMm,
    printAreaRect,
    printAreaWidthMm: widthMm,
    printAreaHeightMm: heightMm,
    // The effective tier, not the raw column: an admin override outranks
    // the width the catalogue reports.
    printAreaSizeTier: printArea ? effectiveSizeTier(printArea) : undefined,
    printAreaSizeTierOverride: printArea?.sizeTierOverride ?? null,
    ...(product ? { productId: product.id } : {}),
    printAreaProductClass: printArea?.productClass ?? product?.productClass,
    printAreaCarryOverPercent: printArea?.carryOverPercent,
    printAreaCarryOverAnchor: printArea?.carryOverAnchor,
    printAreaCarryOverTopOffsetMm: printArea?.carryOverTopOffsetMm,
    printArea: printArea?.printArea ?? {},
  };
}

/**
 * Default render order of the mockup layer stack (bottom → top).
 * Single source of truth lives in @designdeck/shared (shared with the
 * server-side compositor): heathered → design → base → texture.
 */
export const DEFAULT_LAYER_ORDER = DEFAULT_MOCKUP_LAYER_ORDER;

/**
 * Normalize a stored layer order: unknown entries are dropped, missing
 * layers are appended in default order so every layer renders exactly once.
 */
export function normalizeLayerOrder(
  order: readonly string[] | null | undefined
): string[] {
  return normalizeMockupLayerOrder(order);
}

/**
 * Transform API Product to UI Product
 * @param apiProduct - Product from API
 * @param selectedColors - Optional pre-selected color IDs
 * @param isActive - Optional active state
 */
export function transformProduct(
  apiProduct: ApiProduct,
  selectedColors: string[] = [],
  isActive: boolean = false
): UIProduct {
  // Transform colors
  const colors: UIProductColor[] = (apiProduct.colors || []).map(
    transformColor
  );

  // If no colors selected but product has colors, select first one when activated
  const defaultSelectedColors =
    selectedColors.length > 0
      ? selectedColors
      : isActive && colors.length > 0
        ? [colors[0]!.id]
        : [];

  // Extract category slug
  const category = apiProduct.category?.slug || 'uncategorized';
  const categoryId = apiProduct.category?.id;

  // Transform positions to views
  const views: UIProductView[] = (apiProduct.positions || []).map((pos) => ({
    id: pos.id,
    name: pos.slug,
    label: pos.name,
  }));

  // Build position slug → id map for mockup templates
  const positionIdMap = new Map<string, string>();
  // Build position slug → printArea map for canvas sizing
  const printAreaMap = new Map<string, PrintAreaData>();

  // The product as the carry-over rule sees it: id, class and defaults.
  const placementProduct: PlacementAreaProduct = {
    id: apiProduct.id,
    productClass: apiProduct.productClass,
    carryOverPercent: apiProduct.carryOverPercent,
    carryOverAnchor: apiProduct.carryOverAnchor,
    carryOverTopOffsetMm: apiProduct.carryOverTopOffsetMm,
  };

  for (const pos of apiProduct.positions || []) {
    positionIdMap.set(pos.slug, pos.id);

    // Extract PrintArea dimensions if available
    if (pos.printArea) {
      // Effective tier and carry-over settings through the shared rules,
      // so the cards read the area exactly as the API and the renderer do.
      const area = {
        productClass: placementProduct.productClass ?? null,
        ...effectiveCarryOver(placementProduct, pos.printArea),
      };
      printAreaMap.set(pos.slug, {
        // PrintArea keeps its physical size in maxWidthMm/maxHeightMm.
        maxWidthMm: pos.printArea.maxWidthMm ?? undefined,
        maxHeightMm: pos.printArea.maxHeightMm ?? undefined,
        sizeTier: effectiveSizeTier(pos.printArea),
        sizeTierOverride: pos.printArea.sizeTierOverride ?? null,
        productClass: area.productClass,
        carryOverPercent: area.carryOverPercent,
        carryOverAnchor: area.carryOverAnchor,
        carryOverTopOffsetMm: area.carryOverTopOffsetMm,
        printArea: pos.printArea,
      });
    }
  }

  // Transform mockup templates with position ID and PrintArea mapping
  const mockupTemplates: UIProductMockupTemplate[] = (
    apiProduct.mockupTemplates || []
  ).flatMap((template) => {
    const transformed = transformMockupTemplate(
      template,
      positionIdMap,
      printAreaMap,
      placementProduct
    );
    return transformed ? [transformed] : [];
  });

  return {
    id: apiProduct.id,
    name: apiProduct.name,
    blankCost:
      typeof apiProduct.blankCost === 'number' ? apiProduct.blankCost : null,
    startingCost:
      typeof apiProduct.startingCost === 'number'
        ? apiProduct.startingCost
        : null,
    sellingPrice: apiProduct.recommendedSalesPrice
      ? Number(apiProduct.recommendedSalesPrice)
      : undefined,
    colors,
    selectedColors: defaultSelectedColors,
    isActive,
    isPremium: apiProduct.isOrganic, // Organic products as premium
    category,
    categoryId,
    hsCode: apiProduct.hsCode,
    manufacturerId: apiProduct.manufacturer?.id,
    manufacturerName: apiProduct.manufacturer?.name,
    productClass: apiProduct.productClass ?? null,
    carryOverPercent: apiProduct.carryOverPercent ?? null,
    carryOverAnchor: apiProduct.carryOverAnchor ?? null,
    carryOverTopOffsetMm: apiProduct.carryOverTopOffsetMm ?? null,
    matchPositions: (apiProduct.positions || []).map((pos) => {
      const decoration = getDecorationCategoryFromCode(
        pos.decorationMethods?.code
      );
      // Every method the position offers, so a Position printed by default
      // still shows up in an embroidery run. Falls back to the default when
      // the API predates the full list.
      const offered = (pos.availableDecorationMethods ?? [])
        .map((m) => getDecorationCategoryFromCode(m.code))
        .filter((c): c is NonNullable<typeof c> => c !== null);
      return {
        slug: pos.slug,
        maxWidthMm: pos.printArea?.maxWidthMm ?? null,
        sizeTier: pos.printArea ? effectiveSizeTier(pos.printArea) : null,
        // The admin marker and the effective carry-over settings decide which
        // rule the carry-over applies, and with it whether the width check
        // applies at all (relative rule only).
        sizeTierOverride: pos.printArea?.sizeTierOverride ?? null,
        carryOverPercent: printAreaMap.get(pos.slug)?.carryOverPercent ?? null,
        carryOverAnchor: printAreaMap.get(pos.slug)?.carryOverAnchor ?? null,
        carryOverTopOffsetMm:
          printAreaMap.get(pos.slug)?.carryOverTopOffsetMm ?? null,
        minDpi: pos.decorationMethods?.minDpi ?? null,
        decoration,
        decorations:
          offered.length > 0
            ? Array.from(new Set(offered))
            : decoration
              ? [decoration]
              : [],
      };
    }),
    views: views.length > 0 ? views : undefined,
    // Image URLs proxied for CORS in development
    thumbnailUrl: proxyStorageUrl(apiProduct.thumbnailImageUrl) || undefined,
    mockupImageUrl: proxyStorageUrl(apiProduct.mockupImageUrl) || undefined,
    mockupTemplates,
    positions: (apiProduct.positions || []).filter(
      (p) => p.printArea != null
    ) as unknown as ProductPosition[],
  };
}

/**
 * Transform multiple API Products to UI Products
 */
export function transformProducts(
  apiProducts: ApiProduct[],
  productStates?: Map<string, { selectedColors: string[]; isActive: boolean }>
): UIProduct[] {
  return apiProducts.map((product) => {
    const state = productStates?.get(product.id);
    return transformProduct(
      product,
      state?.selectedColors || [],
      state?.isActive || false
    );
  });
}

/**
 * Transform API Category to UI Category format
 */
export interface UICategory {
  id: string;
  name: string;
  slug: string;
  count?: number;
}

export function transformCategory(apiCategory: ApiProductCategory): UICategory {
  return {
    id: apiCategory.id,
    name: apiCategory.name,
    slug: apiCategory.slug,
    count: apiCategory._count?.products,
  };
}

export function transformCategories(
  apiCategories: ApiProductCategory[]
): UICategory[] {
  return apiCategories.map(transformCategory);
}

// ═══════════════════════════════════════════════════════════════════
// MOCKUP TEMPLATE UTILITIES
// ═══════════════════════════════════════════════════════════════════

/**
 * Order the creator offers positions in. Unknown keys sort to the end.
 *
 * Covers every position slug the catalogue can carry (the imported area
 * types of plan KTD13): the four garment views first, then the combined
 * print area and the wrap-around segments, which only appear on products
 * that split a design across front and back.
 */
export const POSITION_ORDER = [
  'front',
  'back',
  'back-inside',
  'sleeve-left',
  'sleeve-right',
  'combined',
  'front-segment',
  'back-segment',
  'front-segment-part',
  'back-segment-part',
];

/**
 * One template per position the product actually configures, in display order.
 *
 * A mockup template alone does not make a designable position: the matching
 * ProductPosition row carries the print area and is what a saved design
 * position points at. Templates without one (e.g. sleeve mockups on a product
 * that never configured sleeves) have no row to save into, so they are
 * dropped here. Every template that reaches the UI is calibrated (see
 * transformMockupTemplate), so an uncalibrated sibling never shows up.
 * Direct Sale applies the same rule by intersecting the sellable positions
 * with the available templates (see DirectSalePositionPills).
 */
export function getConfiguredPositionTemplates(
  templates: UIProductMockupTemplate[]
): UIProductMockupTemplate[] {
  const byPosition = new Map<string, UIProductMockupTemplate>();

  for (const template of templates) {
    if (!template.productPositionId) continue;

    const existing = byPosition.get(template.positionKey);
    if (!existing || template.isDefault) {
      byPosition.set(template.positionKey, template);
    }
  }

  const rank = (positionKey: string) => {
    const index = POSITION_ORDER.indexOf(positionKey);
    return index === -1 ? POSITION_ORDER.length : index;
  };

  return Array.from(byPosition.values()).sort(
    (a, b) => rank(a.positionKey) - rank(b.positionKey)
  );
}

/**
 * Find mockup template by position key (e.g., "front", "back")
 */
export function findTemplateByPosition(
  templates: UIProductMockupTemplate[],
  positionKey: string
): UIProductMockupTemplate | undefined {
  return templates.find((t) => t.positionKey === positionKey);
}

/**
 * Get the default mockup template for a product
 * Falls back to first "front" template or first available template
 */
export function getDefaultTemplate(
  templates: UIProductMockupTemplate[]
): UIProductMockupTemplate | undefined {
  // First, try to find explicitly marked default
  const defaultTemplate = templates.find((t) => t.isDefault);
  if (defaultTemplate) return defaultTemplate;

  // Then, try "front" position
  const frontTemplate = findTemplateByPosition(templates, 'front');
  if (frontTemplate) return frontTemplate;

  // Fallback to first available
  return templates[0];
}

/**
 * Get available position keys from templates
 */
export function getAvailablePositions(
  templates: UIProductMockupTemplate[]
): string[] {
  const uniquePositions = new Set(templates.map((t) => t.positionKey));
  return Array.from(uniquePositions);
}
